import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { PropertiesListResponse, UserListItem } from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { formatLocaleDateTime } from '@/lib/format-utils'
import {
  formatReportCadence,
  formatReportingMode,
  formatUserFullName,
  listUserRowFromInvitePayload,
  userStatusBadgeVariant,
} from '@/lib/user-utils'
import { useToast } from '@/hooks/use-toast'

type PropertyListItem = PropertiesListResponse['properties'][number]

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err))
}

/** The invite dialog's own input shape (not the transport-level InviteUserRequest). */
interface InviteUserFormValues {
  email: string
  name: string
  propertyIds: string[]
}

// The old manual check only ever verified the email was non-empty (after trim) - it never
// validated email format - so this is ported as a non-empty string, not `.email()`, to avoid
// silently tightening what the form accepts.
const inviteUserSchema = z.object({
  email: z.string().trim().min(1, 'Email is required'),
  name: z.string(),
  propertyIds: z.array(z.string()),
})

/**
 * Users list: browse users, invite new users. Editing happens on `/users/:userId`.
 */
export function UsersPage() {
  const { toast } = useToast()
  const [users, setUsers] = useState<UserListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const [properties, setProperties] = useState<PropertyListItem[]>([])
  const [propertiesLoading, setPropertiesLoading] = useState(true)
  const [propertiesError, setPropertiesError] = useState<Error | null>(null)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [invitePropertyQuery, setInvitePropertyQuery] = useState('')
  const [inviteError, setInviteError] = useState<Error | null>(null)

  const inviteForm = useForm<InviteUserFormValues>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: { email: '', name: '', propertyIds: [] },
  })
  const inviteSaving = inviteForm.formState.isSubmitting

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await api.listUsers()
        setUsers(data.users || [])
      } catch (err) {
        setError(toError(err))
      } finally {
        setLoading(false)
      }
    }

    loadUsers()
  }, [])

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const data = await api.getProperties()
        setProperties(data.properties || [])
      } catch (err) {
        setPropertiesError(toError(err))
      } finally {
        setPropertiesLoading(false)
      }
    }

    loadProperties()
  }, [])

  const filteredInviteProperties = useMemo(() => {
    const q = invitePropertyQuery.trim().toLowerCase()
    const list = properties || []
    if (!q) return list
    return list.filter(p => {
      const name = String(p.display_name || p.name || '').toLowerCase()
      const id = String(p.id || p.property_id || '').toLowerCase()
      return name.includes(q) || id.includes(q)
    })
  }, [properties, invitePropertyQuery])

  const openInvite = () => {
    setInviteError(null)
    inviteForm.reset({ email: '', name: '', propertyIds: [] })
    setInvitePropertyQuery('')
    setInviteOpen(true)
  }

  const submitInvite = async (values: InviteUserFormValues) => {
    const email = values.email.trim().toLowerCase()

    setInviteError(null)
    try {
      const result = await api.inviteUser({
        email,
        role: 'portal_user',
        property_ids: values.propertyIds,
        name: values.name.trim() || undefined,
      })
      const refreshed = await api.listUsers()
      const fromInvite = listUserRowFromInvitePayload(result?.user)
      let next: UserListItem[] = refreshed.users || []
      if (fromInvite) {
        const i = next.findIndex(u => u.id === fromInvite.id)
        next =
          i === -1
            ? [...next, fromInvite as unknown as UserListItem]
            : next.map((row, j) => (j === i ? { ...row, ...fromInvite } : row))
      }
      setUsers(next)
      setInviteOpen(false)
      toast({
        title: 'Invite sent',
        description:
          result?.invite_status === 'invite_resent'
            ? `Another password setup email was sent to ${email}.`
            : `Password setup email sent to ${email}.`,
      })
    } catch (err) {
      setInviteError(toError(err))
    }
  }

  if (loading) {
    return <div className="text-sm text-textMuted">Loading users...</div>
  }

  if (error) {
    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-destructive">Failed to load users</div>
        <pre className="rounded-md bg-surface2 p-3 text-xs text-textMuted whitespace-pre-wrap">
          {String(error?.message || error)}
        </pre>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-text">Users</h1>
          <p className="mt-1 text-sm text-textMuted">
            Invite users with an Auth0 password setup email, assign properties, and manage access.
            New users start as invited until they complete password setup; edit status on each
            user&apos;s page.
          </p>
        </div>
        <Button onClick={openInvite}>Invite user</Button>
      </div>

      <div className="rounded-xl border border-border bg-surface p-4 overflow-x-auto">
        <table className="w-full min-w-[1180px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-textMuted">
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium">Email</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Invited</th>
              <th className="py-2 pr-4 font-medium">Last invite sent</th>
              <th className="py-2 pr-4 font-medium">Last portal login</th>
              <th className="py-2 pr-4 font-medium">Report emails</th>
              <th className="py-2 pr-4 font-medium">Cadence</th>
              <th className="py-2 pr-4 font-medium">Revenue</th>
              <th className="py-2 text-right font-medium">Properties</th>
              <th className="py-2 pl-4 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className="border-b last:border-0">
                <td className="py-2 pr-4">{formatUserFullName(user)}</td>
                <td className="py-2 pr-4">{user.email}</td>
                <td className="py-2 pr-4">
                  <Badge variant={userStatusBadgeVariant(user.status)}>{user.status}</Badge>
                </td>
                <td className="py-2 pr-4 text-textMuted">
                  {formatLocaleDateTime(user.invited_at)}
                </td>
                <td className="py-2 pr-4 text-textMuted">
                  {formatLocaleDateTime(user.invite_sent_at)}
                </td>
                <td className="py-2 pr-4 text-textMuted">
                  {formatLocaleDateTime(user.last_portal_login_at || user.last_login_at)}
                </td>
                <td className="py-2 pr-4 text-textMuted">
                  {user.notify_new_reports_published ? 'On' : 'Off'}
                </td>
                <td className="py-2 pr-4 text-textMuted">
                  {user.notify_new_reports_published ? formatReportCadence(user) : '—'}
                </td>
                <td className="py-2 pr-4 text-textMuted">{formatReportingMode(user)}</td>
                <td className="py-2 text-right text-textMuted">
                  {typeof user.property_count === 'number' ? user.property_count : '—'}
                </td>
                <td className="py-2 pl-4 text-right">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    asChild
                    aria-label={`Edit user ${formatUserFullName(user)}`}
                  >
                    <Link to={`/users/${encodeURIComponent(user.id)}`}>
                      <Pencil className="h-4 w-4" />
                    </Link>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>
              Creates or updates the user locally and sends Auth0&apos;s password setup email.
              Disabled users cannot be invited until re-enabled.
            </DialogDescription>
          </DialogHeader>

          <Form {...inviteForm}>
            <form onSubmit={inviteForm.handleSubmit(submitInvite)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="name@example.com" autoComplete="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-textMuted">User type</div>
                    <Input value="portal_user" disabled />
                  </div>
                  <FormField
                    control={inviteForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Full name" autoComplete="name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={inviteForm.control}
                  name="propertyIds"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <FormLabel>Property access</FormLabel>
                          <div className="mt-0.5 text-xs text-textMuted">
                            Selected: {field.value.length}
                          </div>
                        </div>
                        <Input
                          value={invitePropertyQuery}
                          onChange={e => setInvitePropertyQuery(e.target.value)}
                          placeholder="Search properties…"
                        />
                      </div>

                      {propertiesLoading ? (
                        <div className="text-sm text-textMuted">Loading properties…</div>
                      ) : propertiesError ? (
                        <div className="rounded-lg border border-border bg-surface2 p-3 text-xs text-destructive">
                          Failed to load properties:{' '}
                          {String(propertiesError?.message || propertiesError)}
                        </div>
                      ) : (
                        <FormControl>
                          <div className="max-h-[340px] overflow-auto rounded-lg border border-border bg-bg p-2">
                            {filteredInviteProperties.length === 0 ? (
                              <div className="p-3 text-sm text-textMuted">
                                No matching properties.
                              </div>
                            ) : (
                              <ul className="divide-y">
                                {filteredInviteProperties.map(p => {
                                  const id = String(p.id || p.property_id)
                                  const name = p.display_name || p.name || id
                                  const checked = field.value.includes(id)
                                  return (
                                    <li key={id} className="flex items-center gap-3 p-2">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          field.onChange(
                                            checked
                                              ? field.value.filter((x: string) => x !== id)
                                              : [...field.value, id]
                                          )
                                        }}
                                        className="h-4 w-4 accent-mint"
                                      />
                                      <div className="min-w-0">
                                        <div className="truncate text-sm text-text">{name}</div>
                                        <div className="truncate text-xs text-textMuted">{id}</div>
                                      </div>
                                    </li>
                                  )
                                })}
                              </ul>
                            )}
                          </div>
                        </FormControl>
                      )}
                    </FormItem>
                  )}
                />
              </div>

              {inviteError ? (
                <div className="rounded-lg border border-border bg-surface2 p-3 text-xs text-destructive">
                  {String(inviteError?.message || inviteError)}
                </div>
              ) : null}

              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviteSaving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={inviteSaving}>
                  {inviteSaving ? 'Sending…' : 'Send invite'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
